import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Search, Plus, Pencil, Trash2, RefreshCw, Package, Upload, ImageIcon, X, FileUp, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Modal, ConfirmDialog, Spinner, EmptyState, Pagination, StatusBadge } from '../components/ui'
import { productService, categoryService } from '../services/api'
import toast from 'react-hot-toast'

const EMPTY_FORM = { description:'', stock_code:'', category_id:'', inclprice2:'', exclprice2:'', inclprice3:'', exclprice3:'', inclprice4:'', exclprice4:'', inclprice5:'', exclprice5:'', image:'', barcode:'', brand:'', short_description:'', long_description:'', primary_image:'', low_stock_threshold: 10 }

function norm(p) {
  const imgSrc = p.primary_image
    ? `/MappedData/${p.primary_image}`
    : p.image || null
  const price = parseFloat(p.inclprice || p.inclprice2 || p.inclprice3 || 0)
  return { ...p, name: p.description || p.product_name || p.stock_code, price, imgSrc }
}

function ProductThumb({ src, name }) {
  const [err, setErr] = React.useState(false)
  if (!src || err) {
    return (
      <div className="w-10 h-10 rounded-lg bg-black/40 border border-[#323232] flex items-center justify-center text-xl flex-shrink-0">
        🍾
      </div>
    )
  }
  return (
    <img
      src={src} alt={name}
      onError={() => setErr(true)}
      className="w-10 h-10 rounded-lg object-contain bg-black/20 flex-shrink-0 p-0.5"
    />
  )
}

export default function Products() {
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState(() => searchParams.get('search') || '')
  const [catFilter, setCatFilter] = useState('')
  const [brandFilter, setBrandFilter] = useState('')
  const [page, setPage] = useState(1)
  const [editProduct, setEditProduct] = useState(null)
  const [editProductFull, setEditProductFull] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const importRef = useRef(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [perPage, setPerPage] = useState(20)
  const [selectedId, setSelectedId] = useState(null)
  const qc = useQueryClient()

  // Sync URL search param when navigating here from top bar
  useEffect(() => {
    const q = searchParams.get('search') || ''
    if (q) { setSearch(q); setPage(1) }
  }, [searchParams])

  const isSearchMode = search.trim().length > 0

  // Server-side paginated list of ALL products (search + category + paging on the server).
  const { data: pd, isLoading } = useQuery({
    queryKey: ['admin', 'products', 'list', page, perPage, search.trim(), catFilter, brandFilter],
    queryFn: () => productService.getAdminList({
      page,
      limit: perPage,
      search: search.trim() || undefined,
      category_id: catFilter || undefined,
      brand: brandFilter || undefined,
    }),
    keepPreviousData: true,
    staleTime: 60 * 1000,
  })

  const { data: cd } = useQuery({
    queryKey: ['categories'],
    queryFn: categoryService.getAll,
    staleTime: 10 * 60 * 1000,
  })

  const { data: brandsData } = useQuery({
    queryKey: ['product-brands'],
    queryFn: productService.getBrands,
    staleTime: 10 * 60 * 1000,
  })
  const brands = Array.isArray(brandsData) ? brandsData : []

  const createMutation = useMutation({
    mutationFn: productService.create,
    onSuccess: () => { qc.invalidateQueries(['admin','products']); toast.success('Product created'); setShowCreate(false); setForm(EMPTY_FORM) },
    onError: (e) => toast.error(e.message || 'Create failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => productService.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['admin','products']); toast.success('Product updated'); setEditProduct(null); setEditProductFull(null) },
    onError: (e) => toast.error(e.message || 'Update failed'),
  })

  const deleteMutation = useMutation({
    mutationFn: productService.delete,
    onSuccess: () => { qc.invalidateQueries(['admin','products']); toast.success('Product deleted'); setDeleteTarget(null) },
    onError: (e) => toast.error(e.message || 'Delete failed'),
  })

  const raw = Array.isArray(pd) ? pd : (pd?.items ?? pd?.products ?? [])
  const products = raw.map(norm)
  const total = pd?.total ?? raw.length
  const cats = Array.isArray(cd) ? cd : (cd?.items ?? cd?.categories ?? [])

  // Reset to page 1 whenever search / category / page size changes
  useEffect(() => { setPage(1) }, [search, catFilter, brandFilter, perPage])

  const paginated = products   // server returns just this page, already filtered

  const [backfilling, setBackfilling] = useState(false)
  const [backfillPreview, setBackfillPreview] = useState(null)   // dry-run result → opens confirm modal
  const [backfillApplying, setBackfillApplying] = useState(false)
  const handleBackfillBrands = async () => {
    try {
      setBackfilling(true)
      // dry run first to get a count + preview, then open the confirm modal
      const dry = await productService.backfillBrands(false, true)
      if (!dry?.updated) { toast.success('All products already have a brand — nothing to backfill.'); return }
      setBackfillPreview(dry)
    } catch (e) {
      toast.error(e.message || 'Backfill failed')
    } finally {
      setBackfilling(false)
    }
  }
  const handleBackfillConfirm = async () => {
    try {
      setBackfillApplying(true)
      const res = await productService.backfillBrands(true, true)
      toast.success(`Brands set for ${res.updated} products`)
      qc.invalidateQueries(['admin', 'products'])
      qc.invalidateQueries(['product-brands'])
      setBackfillPreview(null)
    } catch (e) {
      toast.error(e.message || 'Backfill failed')
    } finally {
      setBackfillApplying(false)
    }
  }

  const [catBackfilling, setCatBackfilling] = useState(false)
  const [catPreview, setCatPreview] = useState(null)       // dry-run result → opens modal
  const [catApplying, setCatApplying] = useState(false)
  const handleBackfillCategories = async () => {
    try {
      setCatBackfilling(true)
      const dry = await productService.backfillCategories(false, true)
      if (!dry?.updated) { toast.success('No categories could be auto-assigned — nothing to change.'); return }
      setCatPreview(dry)
    } catch (e) {
      toast.error(e.message || 'Category backfill failed')
    } finally {
      setCatBackfilling(false)
    }
  }
  const handleCatConfirm = async () => {
    try {
      setCatApplying(true)
      const res = await productService.backfillCategories(true, true)
      toast.success(`Category set for ${res.updated} products`)
      qc.invalidateQueries(['admin', 'products'])
      setCatPreview(null)
    } catch (e) {
      toast.error(e.message || 'Category backfill failed')
    } finally {
      setCatApplying(false)
    }
  }

  const openEdit = (p) => {
    setEditProduct(p)
    setEditProductFull(null)
    setForm({
      description: p.description||'', stock_code: p.stock_code||'', category_id: p.category_id||'',
      inclprice2: p.inclprice2||'', exclprice2: p.exclprice2||'',
      inclprice3: p.inclprice3||'', exclprice3: p.exclprice3||'',
      inclprice4: p.inclprice4||'', exclprice4: p.exclprice4||'',
      inclprice5: p.inclprice5||'', exclprice5: p.exclprice5||'',
      image: p.image||'', barcode: p.barcode||'', brand: p.brand||'',
      short_description: p.short_description||'', long_description: '',
      primary_image: p.primary_image||'',
      low_stock_threshold: p.low_stock_threshold ?? 10
    })
    // Fetch full product (includes details) separately — use features field for long description
    productService.getById(p.id).then(full => {
      setEditProductFull(full)
      if (full?.details?.features) {
        setForm(f => ({ ...f, long_description: full.details.features }))
      }
    }).catch(() => {})
  }

  const handleSave = async () => {
    const { long_description, ...productFields } = form
    if (editProduct) {
      updateMutation.mutate({ id: editProduct.id, data: productFields })
      // Save long description to product_details.features
      if (long_description !== undefined) {
        try {
          await productService.saveDetails(editProduct.id, { features: long_description })
        } catch {
          toast.error('Product saved but long description failed to save')
        }
      }
    } else {
      createMutation.mutate(productFields)
    }
  }

  const handleImport = async () => {
    if (!importFile) return toast.error('Please select a CSV file first')
    setImporting(true)
    setImportResult(null)
    try {
      const result = await productService.importCsv(importFile)
      const r = result || {}
      const parts = [
        `${r.inserted ?? 0} new`,
        `${r.updated ?? 0} updated`,
        `${r.skipped_zero_price ?? 0} skipped (R0.00)`,
        `${r.skipped_suspended ?? 0} suspended`,
      ]
      if (r.skipped) parts.push(`${r.skipped} other skipped`)
      setImportResult({ ok: true, message: `Imported: ${parts.join(', ')}` })
      qc.invalidateQueries(['admin', 'products'])
      toast.success('Products imported')
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Import failed'
      setImportResult({ ok: false, message: msg })
      toast.error(typeof msg === 'string' ? msg : 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {isSearchMode
              ? `Search results for "${search}" — ${total.toLocaleString()} found`
              : `All Products — ${total.toLocaleString()}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBackfillBrands}
            disabled={backfilling}
            title="Fill empty Brand fields from each product name's first word"
            className="btn-secondary flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <RefreshCw size={14} className={backfilling ? 'animate-spin' : ''}/> {backfilling ? 'Working…' : 'Backfill Brands'}
          </button>
          <button
            onClick={handleBackfillCategories}
            disabled={catBackfilling}
            title="Auto-assign empty Category fields from each product description"
            className="btn-secondary flex items-center gap-2 text-sm disabled:opacity-50"
          >
            <RefreshCw size={14} className={catBackfilling ? 'animate-spin' : ''}/> {catBackfilling ? 'Working…' : 'Backfill Categories'}
          </button>
          <button
            onClick={() => { setShowImport(true); setImportFile(null); setImportResult(null) }}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <FileUp size={14}/> Import CSV
          </button>
          <button onClick={() => { setShowCreate(true); setForm(EMPTY_FORM); setEditProduct(null) }}
            className="btn-primary flex items-center gap-2 text-sm"><Plus size={14}/> Add Product</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface-card border border-surface-border rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, stock code or brand…"
              className="input-field w-full pl-9 text-sm"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                <X size={13}/>
              </button>
            )}
          </div>
          <select
            value={catFilter}
            onChange={e => setCatFilter(e.target.value)}
            className="input-field text-sm sm:w-52 shrink-0"
          >
            <option value="">All Categories</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={brandFilter}
            onChange={e => setBrandFilter(e.target.value)}
            className="input-field text-sm sm:w-52 shrink-0"
          >
            <option value="">All Brands</option>
            {brands.map(bn => <option key={bn} value={bn}>{bn}</option>)}
          </select>
          <button onClick={() => { qc.invalidateQueries(['admin','products']) }} className="btn-secondary flex items-center gap-2 text-sm shrink-0">
            <RefreshCw size={14}/> Refresh
          </button>
        </div>
        {(search || catFilter || brandFilter) && (
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            <span>{total.toLocaleString()} result{total !== 1 ? 's' : ''}</span>
            <button onClick={() => { setSearch(''); setCatFilter(''); setBrandFilter('') }} className="text-primary hover:underline">
              Clear filters
            </button>
          </div>
        )}
      </div>

      <Pagination page={page} total={total} perPage={perPage} onPerPageChange={setPerPage} onChange={setPage} position="top"/>

      {/* Table */}
      <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg"/></div>
        ) : paginated.length === 0 ? (
          <EmptyState icon={Package} title="No products" message="No products match your filters"/>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="admin-thead border-b border-surface-border">
                  {['','ID','Product Name','Brand','Stock Code','Pack','Category','Price (Incl)','Actions'].map(h=>(
                    <th key={h} className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map(p => {
                  const cat = cats.find(c => c.id === p.category_id)
                  return (
                    <tr key={p.id} onClick={() => { setSelectedId(p.id); openEdit(p) }}
                      className={`border-b border-surface-border/50 transition-colors cursor-pointer ${selectedId === p.id ? 'bg-primary/10' : 'hover:bg-white/[0.03]'}`}>
                      <td className="px-4 py-3">
                        <ProductThumb src={p.imgSrc} name={p.name} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{p.id}</td>
                      <td className="px-4 py-3 text-white font-medium max-w-[200px] truncate" title={p.name}>{p.name}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs max-w-[120px] truncate">{p.brand || <span className="text-gray-600">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-primary text-xs">{p.stock_code}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs text-center">{p.packsize > 1 ? `×${p.packsize}` : <span className="text-gray-600">1</span>}</td>
                      <td className="px-4 py-3 text-gray-400">{cat?.name || '—'}</td>
                      <td className="px-4 py-3 font-semibold text-white">{p.price > 0 ? `R ${p.price.toFixed(2)}` : <span className="text-gray-600 text-xs">No price</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={e => { e.stopPropagation(); openEdit(p) }}
                            className="admin-btn-solid inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium hover:opacity-90 transition-opacity">
                            <Pencil size={12}/> Edit
                          </button>
                          <button onClick={e => { e.stopPropagation(); setDeleteTarget(p) }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors">
                            <Trash2 size={12}/> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination page={page} total={total} perPage={perPage} onPerPageChange={setPerPage} onChange={setPage} position="bottom"/>

      {/* Create / Edit Modal */}
      {(showCreate || editProduct) && (
        <Modal title={editProduct ? `Edit Product #${editProduct.id}` : 'Add Product'} onClose={() => { setShowCreate(false); setEditProduct(null); setEditProductFull(null) }}>
          <div className="space-y-5 max-h-[72vh] overflow-y-auto px-1 pr-3">

            {/* Core Fields */}
            <div className="space-y-4">
              <p className="text-[11px] font-semibold text-primary uppercase tracking-widest border-b border-surface-border pb-1">Core Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Description</label>
                  <input type="text" value={form.description||''} onChange={e => setForm(f=>({...f,description:e.target.value}))} placeholder="Product description..." className="input-field w-full text-sm"/>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Stock Code</label>
                  <input type="text" value={form.stock_code||''} onChange={e => setForm(f=>({...f,stock_code:e.target.value}))} placeholder="e.g. FGBR-545-24" className="input-field w-full text-sm"/>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Category</label>
                  <select value={form.category_id||''} onChange={e => setForm(f=>({...f,category_id:e.target.value}))} className="input-field w-full text-sm">
                    <option value="">Select category</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-4">
              <p className="text-[11px] font-semibold text-primary uppercase tracking-widest border-b border-surface-border pb-1">Product Info</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Brand</label>
                  <input type="text" value={form.brand||''} onChange={e => setForm(f=>({...f,brand:e.target.value}))} placeholder="Brand name" className="input-field w-full text-sm"/>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Barcode</label>
                  <input type="text" value={form.barcode||''} onChange={e => setForm(f=>({...f,barcode:e.target.value}))} placeholder="EAN / UPC" className="input-field w-full text-sm"/>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Short Description</label>
                  <input type="text" value={form.short_description||''} onChange={e => setForm(f=>({...f,short_description:e.target.value}))} placeholder="Brief product summary..." className="input-field w-full text-sm"/>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Long Description</label>
                  <textarea
                    value={form.long_description||''}
                    onChange={e => setForm(f=>({...f, long_description: e.target.value}))}
                    placeholder="Full product description — tasting notes, ingredients, serving suggestions, etc."
                    rows={4}
                    className="input-field w-full text-sm resize-y"
                  />
                  <p className="text-xs text-gray-600 mt-1">Stored in product details. Displayed on the product detail page.</p>
                </div>
              </div>

              {/* Multi-Image Upload Section */}
              <MultiImageUploadSection
                productId={editProduct?.id}
                primaryPath={form.primary_image}
                onPrimaryPathChange={(path) => setForm(f => ({ ...f, primary_image: path }))}
                lookupQuery={[form.brand, form.description].filter(Boolean).join(' ').trim()}
              />
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <p className="text-[11px] font-semibold text-primary uppercase tracking-widest border-b border-surface-border pb-1">Pricing</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  ['Incl. Price — Tier 2', 'inclprice2'],
                  ['Excl. Price — Tier 2', 'exclprice2'],
                  ['Incl. Price — Tier 3', 'inclprice3'],
                  ['Excl. Price — Tier 3', 'exclprice3'],
                  ['Incl. Price — Tier 4', 'inclprice4'],
                  ['Excl. Price — Tier 4', 'exclprice4'],
                  ['Incl. Price — Tier 5', 'inclprice5'],
                  ['Excl. Price — Tier 5', 'exclprice5'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-400 mb-1.5 font-medium">{label}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">R</span>
                      <input type="number" step="0.01" min="0" value={form[key]||''} onChange={e => setForm(f=>({...f,[key]:e.target.value}))} placeholder="0.00" className="input-field w-full text-sm pl-6"/>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stock Alert */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold text-primary uppercase tracking-widest border-b border-surface-border pb-1">Stock Management</p>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Stock Alert Threshold</label>
                <input
                  type="number" min="0" step="1"
                  value={form.low_stock_threshold ?? 10}
                  onChange={e => setForm(f => ({ ...f, low_stock_threshold: parseInt(e.target.value) || 0 }))}
                  className="input-field w-40 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Notify when stock falls below this number</p>
              </div>
            </div>

            {/* Scraped Data (read-only) — only shown when editing */}
            {editProductFull?.details && (
              <div className="space-y-3">
                <p className="text-[11px] font-semibold text-primary uppercase tracking-widest border-b border-surface-border pb-1">Scraped Data (read-only)</p>
                <div className="bg-black/20 border border-surface-border rounded-xl p-4 space-y-2 text-xs">
                  {[
                    ['Alcohol Content', editProductFull.details.alcohol_content],
                    ['Volume', editProductFull.details.volume_ml ? `${editProductFull.details.volume_ml}ml` : null],
                    ['Country', editProductFull.details.country_of_origin],
                    ['Year', editProductFull.details.year],
                    ['Allergens', editProductFull.details.allergens],
                    ['Certifications', editProductFull.details.certifications],
                    ['Tasting Notes', editProductFull.details.tasting_notes],
                    ['Serving Suggestions', editProductFull.details.serving_suggestions],
                    ['Storage Info', editProductFull.details.storage_info],
                    ['Ingredients', editProductFull.details.ingredients],
                    ['Warnings', editProductFull.details.warnings],
                  ].filter(([_, v]) => v).map(([label, val]) => (
                    <div key={label} className="flex gap-3">
                      <span className="text-gray-500 w-36 flex-shrink-0 font-medium">{label}:</span>
                      <span className="text-gray-300 break-words">{val}</span>
                    </div>
                  ))}
                  {!Object.values(editProductFull.details).some(Boolean) && (
                    <p className="text-gray-500 italic">No scraped data available for this product.</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-3 border-t border-surface-border">
              <button onClick={() => { setShowCreate(false); setEditProduct(null); setEditProductFull(null) }} className="btn-ghost flex-1 text-sm">Cancel</button>
              <button onClick={handleSave} disabled={createMutation.isLoading || updateMutation.isLoading} className="btn-primary flex-1 text-sm">
                {(createMutation.isLoading || updateMutation.isLoading) ? <Spinner size="sm"/> : editProduct ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Product?"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This cannot be undone.`}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteMutation.isLoading}
        />
      )}

      {/* ─── Backfill Brands confirm modal ─── */}
      {backfillPreview && (
        <Modal title="Backfill Brands" onClose={() => !backfillApplying && setBackfillPreview(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-300">
              Set the brand from the product name for{' '}
              <span className="font-semibold text-white">{(backfillPreview.updated || 0).toLocaleString()}</span>{' '}
              products that have no brand yet?
            </p>
            {backfillPreview.samples?.length > 0 && (
              <div className="bg-black/20 border border-surface-border rounded-xl p-3 space-y-1.5">
                <p className="text-[11px] font-semibold text-primary uppercase tracking-widest">Examples</p>
                {backfillPreview.samples.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400 truncate max-w-[220px]" title={s.name}>{s.name}</span>
                    <span className="text-gray-600 shrink-0">→</span>
                    <span className="text-white font-medium shrink-0">{s.to}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500">Only blank brand fields are touched. You can edit any afterwards.</p>
            <div className="flex gap-3 pt-2 border-t border-surface-border">
              <button onClick={() => setBackfillPreview(null)} disabled={backfillApplying} className="btn-ghost flex-1 text-sm">Cancel</button>
              <button onClick={handleBackfillConfirm} disabled={backfillApplying}
                className="btn-primary flex-1 text-sm flex items-center justify-center gap-1.5">
                {backfillApplying ? <Spinner size="sm"/> : <><RefreshCw size={14}/> Set {(backfillPreview.updated || 0).toLocaleString()} brands</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── Backfill Categories confirm modal ─── */}
      {catPreview && (
        <Modal title="Backfill Categories" onClose={() => !catApplying && setCatPreview(null)} size="md">
          <div className="space-y-4">
            <p className="text-sm text-gray-300">
              Auto-assign a category to{' '}
              <span className="font-semibold text-white">{(catPreview.updated || 0).toLocaleString()}</span>{' '}
              products with no category yet, from their description?
              {catPreview.unmatched > 0 && (
                <span className="text-gray-500"> ({catPreview.unmatched.toLocaleString()} couldn’t be classified and will stay blank.)</span>
              )}
            </p>
            {catPreview.by_category?.length > 0 && (
              <div className="bg-black/20 border border-surface-border rounded-xl p-3">
                <p className="text-[11px] font-semibold text-primary uppercase tracking-widest mb-2">Breakdown by category</p>
                <div className="max-h-52 overflow-y-auto grid grid-cols-2 gap-x-4 gap-y-1">
                  {catPreview.by_category.map(b => (
                    <div key={b.category} className="flex items-center justify-between text-xs">
                      <span className="text-gray-300 truncate" title={b.category}>{b.category}</span>
                      <span className="text-white font-mono shrink-0 ml-2">{b.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {catPreview.samples?.length > 0 && (
              <div className="bg-black/20 border border-surface-border rounded-xl p-3 space-y-1.5">
                <p className="text-[11px] font-semibold text-primary uppercase tracking-widest">Examples</p>
                {catPreview.samples.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-gray-400 truncate max-w-[220px]" title={s.name}>{s.name}</span>
                    <span className="text-gray-600 shrink-0">→</span>
                    <span className="text-white font-medium shrink-0">{s.to}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500">Only products with no category are touched. You can edit any afterwards.</p>
            <div className="flex gap-3 pt-2 border-t border-surface-border">
              <button onClick={() => setCatPreview(null)} disabled={catApplying} className="btn-ghost flex-1 text-sm">Cancel</button>
              <button onClick={handleCatConfirm} disabled={catApplying}
                className="btn-primary flex-1 text-sm flex items-center justify-center gap-1.5">
                {catApplying ? <Spinner size="sm"/> : <><RefreshCw size={14}/> Set {(catPreview.updated || 0).toLocaleString()} categories</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── Import CSV Modal ─── */}
      {showImport && (
        <Modal title="Import Stock List (CSV)" onClose={() => setShowImport(false)} size="md">
          <div className="space-y-5">
            <div className="bg-black/20 border border-[#323232] rounded-xl p-4 text-xs text-gray-400 space-y-1.5">
              <p className="text-white font-medium text-sm mb-2">Expected CSV Columns</p>
              <p><code className="text-primary">StockCode</code>, <code className="text-primary">Description</code>, <code className="text-primary">Department</code>, <code className="text-primary">PackSize</code></p>
              <p><code className="text-primary">ExclPrice2</code>, <code className="text-primary">InclPrice2</code>, <code className="text-primary">ExclPrice3</code>, <code className="text-primary">InclPrice3</code></p>
              <p><code className="text-primary">ExclPrice4</code>, <code className="text-primary">InclPrice4</code>, <code className="text-primary">ExclPrice5</code>, <code className="text-primary">InclPrice5</code></p>
              <p className="text-gray-500 mt-1">Existing products are updated. New stock codes are created. Prices, departments and descriptions are overwritten.</p>
            </div>

            {/* File drop zone */}
            <div
              onClick={() => importRef.current?.click()}
              className={`border-2 border-dashed rounded-xl px-6 py-8 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
                importFile ? 'border-primary/50 bg-primary/5' : 'border-[#323232] hover:border-primary/40 hover:bg-black/20'
              }`}
            >
              <FileUp size={28} className={importFile ? 'text-primary' : 'text-gray-500'}/>
              {importFile ? (
                <div className="text-center">
                  <p className="text-white text-sm font-medium">{importFile.name}</p>
                  <p className="text-gray-400 text-xs mt-1">{(importFile.size / 1024).toFixed(1)} KB — click to change</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-gray-300"><span className="text-primary font-semibold">Click to select</span> your stock CSV file</p>
                  <p className="text-xs text-gray-500 mt-1">CSV files only</p>
                </div>
              )}
              <input
                ref={importRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null) }}
              />
            </div>

            {/* Result */}
            {importResult && (
              <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${
                importResult.ok
                  ? 'bg-green-500/10 border-green-500/20 text-green-300'
                  : 'bg-red-500/10 border-red-500/20 text-red-300'
              }`}>
                {importResult.ok ? <CheckCircle size={16} className="shrink-0 mt-0.5"/> : <X size={16} className="shrink-0 mt-0.5"/>}
                {importResult.message}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1 border-t border-surface-border">
              <button onClick={() => setShowImport(false)} className="btn-secondary">Close</button>
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="btn-primary flex items-center gap-1.5 disabled:opacity-50"
              >
                {importing ? (
                  <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Importing…</>
                ) : (
                  <><FileUp size={14}/> Import Stock List</>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── Multi-Image Upload Section ────────────────────────────────────
function MultiImageUploadSection({ productId, primaryPath, onPrimaryPathChange, lookupQuery = '' }) {
  const [uploadingPrimary, setUploadingPrimary] = useState(false)
  const [uploadingAdditional, setUploadingAdditional] = useState(false)
  const [additionalImages, setAdditionalImages] = useState([])
  const [loadingAdditional, setLoadingAdditional] = useState(false)
  const [dragOverPrimary, setDragOverPrimary] = useState(false)
  const [dragOverAdditional, setDragOverAdditional] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const primaryRef = useRef(null)
  const additionalRef = useRef(null)

  const reloadAdditional = () => {
    if (!productId) return
    setLoadingAdditional(true)
    productService.getImages(productId)
      .then(res => {
        const imgs = res?.data?.images || res?.images || []
        // exclude the primary from the additional list
        const extra = imgs.filter(img => !img.path.endsWith('primary.webp'))
        setAdditionalImages(extra)
      })
      .catch(() => {})
      .finally(() => setLoadingAdditional(false))
  }

  // Load existing additional images when productId is set
  useEffect(() => { reloadAdditional() }, [productId])

  // Apply one or more scraped images: first → primary, rest → additional.
  const handleApplyScraped = async (relPaths) => {
    if (!productId) { toast.error('Save the product first before applying catalogue images'); return }
    if (!relPaths?.length) return
    const res = await productService.applyScrapedImages(productId, relPaths, true)
    if (res?.primary_image) onPrimaryPathChange(res.primary_image)
    const n = res?.applied?.length || relPaths.length
    toast.success(`${n} image${n !== 1 ? 's' : ''} applied from catalogue`)
    reloadAdditional()
    setPickerOpen(false)
  }

  const handlePrimaryFile = async (file) => {
    if (!file) return
    if (!productId) { toast.error('Save the product first before uploading an image'); return }
    setUploadingPrimary(true)
    try {
      const res = await productService.uploadImage(productId, file)
      const path = res?.data?.path || res?.path
      if (path) { onPrimaryPathChange(path); toast.success('Primary image uploaded & converted to WebP') }
    } catch { toast.error('Primary image upload failed') }
    finally { setUploadingPrimary(false) }
  }

  const handleAdditionalFile = async (file) => {
    if (!file) return
    if (!productId) { toast.error('Save the product first before uploading images'); return }
    setUploadingAdditional(true)
    try {
      const res = await productService.uploadAdditionalImage(productId, file)
      const img = res?.data || res
      if (img?.path) {
        setAdditionalImages(prev => [...prev, { path: img.path, url: img.url }])
        toast.success(`Additional image saved as ${img.path.split('/').pop()}`)
      }
    } catch { toast.error('Additional image upload failed') }
    finally { setUploadingAdditional(false) }
  }

  const primarySrc = primaryPath ? `/MappedData/${primaryPath}` : null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-surface-border pb-1">
        <p className="text-[11px] font-semibold text-primary uppercase tracking-widest flex items-center gap-1.5">
          <ImageIcon size={12}/> Product Images
        </p>
        <button
          type="button"
          onClick={() => productId ? setPickerOpen(true) : toast.error('Save the product first to search the image catalogue')}
          className="admin-btn-solid inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium hover:opacity-90 transition-opacity"
          title="Search the scraped-image catalogue and apply images to this product"
        >
          <Search size={12}/> Find in catalogue
        </button>
      </div>

      {/* Primary image */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5 font-medium">
          Primary Image <span className="text-gray-600 font-normal">(shown on product cards — auto-converts to WebP)</span>
        </label>
        <div className="flex items-start gap-3">
          {/* Thumbnail */}
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 rounded-lg border border-[#323232] bg-black/30 flex items-center justify-center overflow-hidden">
              {primarySrc ? (
                <img src={primarySrc} alt="Primary" className="w-full h-full object-contain p-1"
                  onError={e => { e.target.style.display='none' }}/>
              ) : (
                <ImageIcon size={24} className="text-gray-600"/>
              )}
              {uploadingPrimary && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                </div>
              )}
            </div>
            {primarySrc && (
              <button onClick={() => onPrimaryPathChange('')}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                title="Remove primary image"><X size={10}/></button>
            )}
          </div>
          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOverPrimary(true) }}
            onDragLeave={() => setDragOverPrimary(false)}
            onDrop={e => { e.preventDefault(); setDragOverPrimary(false); const f = e.dataTransfer.files?.[0]; if (f?.type?.startsWith('image/')) handlePrimaryFile(f) }}
            onClick={() => primaryRef.current?.click()}
            className={`flex-1 border-2 border-dashed rounded-lg px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${
              dragOverPrimary ? 'border-primary bg-primary/10' : 'border-[#323232] hover:border-primary/50 hover:bg-black/30'
            }`}
          >
            <Upload size={15} className="text-gray-400 flex-shrink-0"/>
            <div className="text-xs text-gray-400">
              <span className="text-primary font-semibold">Click to upload</span> or drag & drop
              <div className="text-[10px] text-gray-500 mt-0.5">JPEG · PNG · WebP — max 5 MB</div>
            </div>
            <input ref={primaryRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden" onChange={e => handlePrimaryFile(e.target.files?.[0])}/>
          </div>
        </div>
        {/* Manual path fallback */}
        <div className="mt-2">
          <label className="block text-[10px] text-gray-500 mb-1">Or enter path manually</label>
          <input type="text" value={primaryPath || ''} onChange={e => onPrimaryPathChange(e.target.value)}
            className="input-field w-full text-xs" placeholder="STOCK-CODE/primary.webp"/>
        </div>
      </div>

      {/* Additional images */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5 font-medium">
          Additional Images <span className="text-gray-600 font-normal">(gallery / detail view — auto-converts to WebP)</span>
        </label>

        {/* Existing additional thumbnails */}
        {loadingAdditional ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2"><div className="w-3 h-3 border border-gray-500 border-t-transparent rounded-full animate-spin"/>Loading…</div>
        ) : additionalImages.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-2">
            {additionalImages.map((img, i) => (
              <div key={img.path} className="relative group">
                <div className="w-16 h-16 rounded-lg border border-[#323232] bg-black/30 overflow-hidden">
                  <img src={img.url} alt={`img_${i+1}`} className="w-full h-full object-contain p-0.5"
                    onError={e => { e.target.style.display='none' }}/>
                </div>
                <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-gray-500 bg-black/60 rounded-b-lg py-0.5 truncate px-1">
                  {img.path.split('/').pop()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          productId && <p className="text-xs text-gray-600 mb-2">No additional images yet.</p>
        )}

        {/* Additional upload zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOverAdditional(true) }}
          onDragLeave={() => setDragOverAdditional(false)}
          onDrop={e => { e.preventDefault(); setDragOverAdditional(false); const f = e.dataTransfer.files?.[0]; if (f?.type?.startsWith('image/')) handleAdditionalFile(f) }}
          onClick={() => additionalRef.current?.click()}
          className={`border-2 border-dashed rounded-lg px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${
            dragOverAdditional ? 'border-primary bg-primary/10' : 'border-[#323232] hover:border-primary/50 hover:bg-black/30'
          }`}
        >
          {uploadingAdditional
            ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0"/>
            : <Plus size={15} className="text-gray-400 flex-shrink-0"/>
          }
          <div className="text-xs text-gray-400">
            <span className="text-primary font-semibold">{uploadingAdditional ? 'Uploading…' : 'Add another image'}</span>
            {!uploadingAdditional && <div className="text-[10px] text-gray-500 mt-0.5">Each upload auto-numbered img_1.webp, img_2.webp…</div>}
          </div>
          <input ref={additionalRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden" onChange={e => handleAdditionalFile(e.target.files?.[0])}/>
        </div>
        {!productId && (
          <p className="text-[10px] text-gray-600 mt-1">Save the product first to enable image uploads.</p>
        )}
      </div>

      {pickerOpen && (
        <ScrapedImagePicker
          initialQuery={lookupQuery}
          onApply={handleApplyScraped}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}

// ── Scraped-image catalogue picker ────────────────────────────────
// Search the server-hosted scraped-image corpus, ranked best-to-worst,
// multi-select (ordered — first pick becomes primary), then Apply.
function ScrapedImagePicker({ initialQuery = '', onApply, onClose }) {
  const [q, setQ] = useState(initialQuery)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [includeThumbs, setIncludeThumbs] = useState(false)
  const [selected, setSelected] = useState([])   // ordered array of rel_paths
  const [applying, setApplying] = useState(false)
  const reqSeq = useRef(0)

  const runSearch = async (query = q, thumbs = includeThumbs) => {
    const term = (query || '').trim()
    if (!term) { setItems([]); setTotal(0); setSearched(false); return }
    const my = ++reqSeq.current
    setLoading(true)
    try {
      const res = await productService.imageLookup({ q: term, include_thumbnails: thumbs, limit: 60 })
      if (my !== reqSeq.current) return   // a newer keystroke superseded this search
      setItems(res?.items || [])
      setTotal(res?.total || 0)
    } catch (e) {
      if (my === reqSeq.current) { toast.error(e.message || 'Image search failed'); setItems([]) }
    } finally {
      if (my === reqSeq.current) { setLoading(false); setSearched(true) }
    }
  }

  const clearSearch = () => { reqSeq.current++; setQ(''); setItems([]); setTotal(0); setSearched(false); setLoading(false) }

  // Search-as-you-type (debounced, min 2 chars). Also covers the initial prefill
  // and re-runs when the thumbnails toggle changes.
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { reqSeq.current++; setItems([]); setTotal(0); setSearched(false); setLoading(false); return }
    const t = setTimeout(() => runSearch(q, includeThumbs), 350)
    return () => clearTimeout(t)
  }, [q, includeThumbs])

  const toggle = (rel) => {
    setSelected(prev => prev.includes(rel) ? prev.filter(r => r !== rel) : [...prev, rel])
  }

  const handleApply = async () => {
    if (!selected.length) return
    setApplying(true)
    try { await onApply(selected) }
    finally { setApplying(false) }
  }

  return (
    <Modal title="Find Product Images" onClose={onClose} size="lg">
      <div className="space-y-4">
        {/* Search bar */}
        <form onSubmit={e => { e.preventDefault(); runSearch() }} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"/>
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search the catalogue — e.g. coca cola, jameson, castle lite…"
              className="input-field w-full pl-9 pr-9 text-sm"
            />
            {q && (
              <button type="button" onClick={clearSearch} title="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                <X size={14}/>
              </button>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary text-sm px-4 disabled:opacity-50">
            {loading ? <Spinner size="sm"/> : 'Search'}
          </button>
        </form>

        <div className="flex items-center justify-between text-xs">
          <label className="flex items-center gap-1.5 text-gray-400 cursor-pointer select-none">
            <input type="checkbox" checked={includeThumbs}
              onChange={e => setIncludeThumbs(e.target.checked)}/>
            Include small thumbnails
          </label>
          {searched && !loading && (
            <span className="text-gray-500">
              {total.toLocaleString()} match{total !== 1 ? 'es' : ''}{total > items.length ? ` — showing best ${items.length}` : ''}
            </span>
          )}
        </div>

        {/* Results grid */}
        <div className="min-h-[220px] max-h-[48vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Spinner size="lg"/></div>
          ) : !searched ? (
            <div className="text-center py-16 text-gray-500 text-sm">Type at least 2 letters to search the catalogue.</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-sm">
              No images found{q.trim() ? ` for "${q.trim()}"` : ''}. Try a shorter or different term.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {items.map(img => {
                const order = selected.indexOf(img.rel_path)
                const isSel = order !== -1
                return (
                  <button
                    type="button"
                    key={img.rel_path}
                    onClick={() => toggle(img.rel_path)}
                    className={`relative aspect-square rounded-lg border-2 overflow-hidden bg-black/30 group transition-colors ${
                      isSel ? 'border-primary' : 'border-[#323232] hover:border-primary/50'
                    }`}
                    title={`${img.product_name || ''}${img.longest_side ? ` — ${img.width}×${img.height}` : ''}`}
                  >
                    <img src={img.url} alt={img.product_name || ''} loading="lazy"
                      className="w-full h-full object-contain p-1"
                      onError={e => { e.currentTarget.style.opacity = 0.2 }}/>
                    {/* dimension / thumbnail badge */}
                    <span className="absolute bottom-0 left-0 right-0 text-[9px] text-gray-300 bg-black/70 px-1 py-0.5 truncate text-center">
                      {img.is_thumbnail ? 'thumb' : (img.longest_side ? `${img.width}×${img.height}` : '—')}
                    </span>
                    {/* selection order badge */}
                    {isSel && (
                      <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shadow">
                        {order + 1}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 pt-3 border-t border-surface-border">
          <p className="text-xs text-gray-500 flex-1">
            {selected.length
              ? `${selected.length} selected — first (①) becomes the primary image, the rest are added to the gallery.`
              : 'Click images to select. The first you pick becomes the primary image.'}
          </p>
          {selected.length > 0 && (
            <button onClick={() => setSelected([])} className="btn-ghost text-sm">Clear</button>
          )}
          <button onClick={handleApply} disabled={!selected.length || applying}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            {applying ? <Spinner size="sm"/> : <><CheckCircle size={14}/> Apply {selected.length || ''}</>}
          </button>
        </div>
      </div>
    </Modal>
  )
}
